import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

type LazyModule<P extends object> = { default: ComponentType<P> }

type StaleChunkReloadEnvironment = {
  entrypointKey: string
  getSessionValue: (key: string) => string | null
  setSessionValue: (key: string, value: string) => void
  reload: () => void
}

const STALE_CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\w-]+ failed/i,
  /error loading dynamically imported module/i,
]

const inMemoryReloadAttempts = new Set<string>()

export const isLikelyStaleChunkError = (error: unknown): boolean => {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : ''
  return STALE_CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export const getStaleChunkReloadKey = (label: string, entrypointKey: string): string => {
  const safeLabel = label.replace(/[^a-z0-9_-]/gi, '-').slice(0, 80) || 'unknown'
  const safeEntrypoint = entrypointKey.replace(/[^a-z0-9_.:/-]/gi, '-').slice(-160) || 'unknown'
  return `timelining:stale-chunk-reload:${safeEntrypoint}:${safeLabel}`
}

const findCurrentEntrypointKey = (): string => {
  if (typeof document === 'undefined') return 'no-document'
  const scripts = Array.from(document.scripts)
    .map((script) => script.src)
    .filter(Boolean)
  return scripts.reverse().find((src) => /\/assets\/(?:js\/)?index-[^/]+\.js(?:\?|$)/.test(src)) ??
    window.location.href
}

const getBrowserReloadEnvironment = (): StaleChunkReloadEnvironment | null => {
  if (typeof window === 'undefined') return null
  return {
    entrypointKey: findCurrentEntrypointKey(),
    getSessionValue: (key) => window.sessionStorage.getItem(key),
    setSessionValue: (key, value) => window.sessionStorage.setItem(key, value),
    reload: () => window.location.reload(),
  }
}

export const reloadOnceForStaleChunk = (
  label: string,
  error: unknown,
  environment = getBrowserReloadEnvironment(),
): boolean => {
  if (!isLikelyStaleChunkError(error) || !environment) return false

  const reloadKey = getStaleChunkReloadKey(label, environment.entrypointKey)
  if (inMemoryReloadAttempts.has(reloadKey)) return false

  try {
    if (environment.getSessionValue(reloadKey) === '1') return false
  } catch {
    // Storage can be unavailable in hardened browsers. The in-memory guard
    // still prevents repeated reload attempts in the current page instance.
  }

  inMemoryReloadAttempts.add(reloadKey)
  try {
    environment.setSessionValue(reloadKey, '1')
  } catch {
    // Best effort only; reload recovery should not fail because storage does.
  }

  try {
    environment.reload()
    return true
  } catch {
    return false
  }
}

export const lazyWithStaleChunkRetry = <P extends object>(
  label: string,
  importer: () => Promise<LazyModule<P>>,
): LazyExoticComponent<ComponentType<P>> =>
  lazy(() =>
    importer().catch((error) => {
      if (reloadOnceForStaleChunk(label, error)) {
        return new Promise<LazyModule<P>>(() => {})
      }
      throw error
    }),
  )

export const installStaleChunkReloadHandler = () => {
  if (typeof window === 'undefined') return
  window.addEventListener('vite:preloadError', (event) => {
    const preloadEvent = event as Event & { payload?: unknown; reason?: unknown }
    const error = preloadEvent.payload ?? preloadEvent.reason
    if (reloadOnceForStaleChunk('vite-preload', error)) {
      event.preventDefault()
    }
  })
}
